import cookies from 'browser-cookies';
import { Adp, AdpAnalytics } from '@adsk/adp-web-analytics-sdk';
import { BERNOULLI_ADP_BY_ENV } from '@/config/adp.site-config';
import type { AdpContext } from '@/services/userContextApi';

const AUTH_TOKEN_COOKIE_KEY = 'auth0.user.token';
const TENANT_ID_SESSION_KEY = 'tenantId';

type JwtPayload = {
  sub?: string;
};

export type RuntimeAdpConfig = {
  name?: string;
  id?: string;
  id_provider?: 'appkey' | 'cls' | 'o2' | 'upi1' | 'upi2';
  key?: string;
  line_name?: string;
  env?: 'dev' | 'stg' | 'prd';
  region?: string;
};

let bernoulliAdp: AdpAnalytics | null = null;
let bernoulliLoaded = false;
let bernoulliConsentInitialized = false;
let bernoulliConsentReady: Promise<void> | null = null;
let adpContext: AdpContext | null = null;

export const setAdpContext = (context: AdpContext) => {
  adpContext = context;
};

export const ADP_EVENTS = {
  // App lifecycle
  APP_ENTRY: 'bernoulli.app.entered',
  APP_FOREGROUND_DURATION: 'bernoulli.app.foregroundDuration',

  // Navigation
  NAV_NEW_CHAT: 'bernoulli.nav.newChat',
  NAV_OPEN_SKILLS: 'bernoulli.nav.openSkills',
  NAV_OPEN_KNOWLEDGE: 'bernoulli.nav.openKnowledge',

  // Terms & Conditions
  TNC_ACCEPT: 'bernoulli.tnc.accept',
  TNC_DECLINE: 'bernoulli.tnc.decline',
  TNC_ACCEPT_ERROR: 'bernoulli.tnc.acceptError',
  TNC_LINK_CLICK: 'bernoulli.tnc.linkClick',

  // Chat core
  CHAT_SEND_MESSAGE: 'bernoulli.chat.sendMessage',
  CHAT_SEND_ERROR: 'bernoulli.chat.sendError',
  CHAT_COPY_PROMPT: 'bernoulli.chat.copyPrompt',
  CHAT_EDIT_PROMPT_OPEN: 'bernoulli.chat.editPromptOpen',
  CHAT_EDIT_PROMPT_SEND: 'bernoulli.chat.editPromptSend',
  CHAT_CANCEL_GENERATION: 'bernoulli.chat.cancelGeneration',
  CHAT_CLEAR_INPUT: 'bernoulli.chat.clearInput',
  CHAT_SCROLL_TO_BOTTOM: 'bernoulli.chat.scrollToBottom',
  CHAT_ERROR_DISMISS: 'bernoulli.chat.errorDismiss',

  // Chat — file upload
  CHAT_UPLOAD_OPEN: 'bernoulli.chat.upload.openPicker',
  CHAT_UPLOAD_REMOVE: 'bernoulli.chat.upload.removeFile',
  CHAT_UPLOAD_DROP: 'bernoulli.chat.upload.dropFiles',
  CHAT_UPLOAD_BLOCKED: 'bernoulli.chat.upload.blocked',
  CHAT_UPLOAD_ERROR: 'bernoulli.chat.upload.error',

  // Chat — history
  CHAT_HISTORY_OPEN: 'bernoulli.chat.history.openConversation',
  CHAT_HISTORY_DELETE: 'bernoulli.chat.history.deleteConversation',
  CHAT_HISTORY_CLEAR: 'bernoulli.chat.history.clearAll',
  CHAT_HISTORY_DELETE_PROMPT: 'bernoulli.chat.history.deletePrompt',
  CHAT_HISTORY_CLEAR_PROMPT: 'bernoulli.chat.history.clearPrompt',
  CHAT_HISTORY_CONFIRM_CANCEL: 'bernoulli.chat.history.confirmCancel',
  CHAT_HISTORY_LOAD_ERROR: 'bernoulli.chat.history.loadError',
  CHAT_HISTORY_SIDEBAR_TOGGLE: 'bernoulli.chatHistory.sidebarToggle',

  // Chat — suggested actions
  CHAT_SUGGESTED_FOLLOWUP: 'bernoulli.chat.suggestedFollowup',
  CHAT_SUGGESTED_CATEGORY: 'bernoulli.chat.suggestedCategory',
  CHAT_SUGGESTED_CATEGORY_PROMPT: 'bernoulli.chat.suggestedCategoryPrompt',
  CHAT_SUGGESTED_CATEGORY_CLOSE: 'bernoulli.chat.suggestedCategoryClose',

  // Feedback
  FEEDBACK_THUMBS_UP: 'bernoulli.feedback.thumbsUp',
  FEEDBACK_THUMBS_DOWN: 'bernoulli.feedback.thumbsDown',
  FEEDBACK_SUBMIT: 'bernoulli.feedback.submit',
  FEEDBACK_SUBMIT_ERROR: 'bernoulli.feedback.submitError',
  FEEDBACK_COPY_RESPONSE: 'bernoulli.feedback.copyResponse',

  // Skills — page & navigation
  SKILLS_OPEN_PAGE: 'bernoulli.skills.openPage',
  SKILLS_CLOSE_PAGE: 'bernoulli.skills.closePage',
  SKILLS_TAB_SWITCH: 'bernoulli.skills.tabSwitch',
  SKILLS_EDITOR_BACK: 'bernoulli.skills.editorBack',

  // Skills — picker & slash menu
  SKILLS_PICKER_TOGGLE: 'bernoulli.skills.pickerToggle',
  SKILLS_PICKER_TOGGLE_SKILL: 'bernoulli.skills.pickerToggleSkill',
  SKILLS_PICKER_OPEN_LIBRARY: 'bernoulli.skills.pickerOpenLibrary',
  SKILLS_SLASH_MENU_OPEN: 'bernoulli.skills.slashMenuOpen',
  SKILLS_SLASH_MENU_SELECT: 'bernoulli.skills.slashMenuSelect',
  SKILLS_REMOVE_ACTIVE: 'bernoulli.skills.removeActive',

  // Skills — CRUD
  SKILLS_CREATE_START: 'bernoulli.skills.createStart',
  SKILLS_OPEN_EDITOR: 'bernoulli.skills.openEditor',
  SKILLS_SAVE: 'bernoulli.skills.save',
  SKILLS_SAVE_ERROR: 'bernoulli.skills.saveError',
  SKILLS_DELETE: 'bernoulli.skills.delete',
  SKILLS_DELETE_ERROR: 'bernoulli.skills.deleteError',
  SKILLS_SYSTEM_TOGGLE: 'bernoulli.skills.systemToggle',
  SKILLS_SYSTEM_TOGGLE_ERROR: 'bernoulli.skills.systemToggleError',

  // Skills — stream result
  SKILL_APPLIED: 'bernoulli.chat.skillApplied',

  // Reasoning / transparency
  REASONING_TOGGLE: 'bernoulli.reasoning.toggle',
  REASONING_STEP_TOGGLE: 'bernoulli.reasoning.stepToggle',
  REASONING_TOOL_TOGGLE: 'bernoulli.reasoning.toolToggle',
  REASONING_TRANSPARENCY_TOGGLE: 'bernoulli.reasoning.transparencyToggle',

  // Content blocks
  CONTENT_COPY_CODE: 'bernoulli.content.copyCode',
  CONTENT_CSV_PREVIEW_TOGGLE: 'bernoulli.content.csvPreviewToggle',
  CHART_EXPORT_DATA: 'bernoulli.chart.exportData',

  // UI Resource (MCP)
  UI_RESOURCE_EXPAND_TOGGLE: 'bernoulli.uiResource.expandToggle',
  UI_RESOURCE_BACKDROP_CLOSE: 'bernoulli.uiResource.backdropClose',
  UI_RESOURCE_PROMPT_SUBMIT: 'bernoulli.uiResource.promptSubmit',
  UI_RESOURCE_OPEN_LINK: 'bernoulli.uiResource.openLink',

  // Drain detection
  DRAIN_DETECTION_OPEN_LIGHTBOX: 'bernoulli.drainDetection.openLightbox',
  DRAIN_DETECTION_CLOSE_LIGHTBOX: 'bernoulli.drainDetection.closeLightbox',
  DRAIN_DETECTION_ZOOM: 'bernoulli.drainDetection.zoom',
  DRAIN_DETECTION_DEFECTS_TOGGLE: 'bernoulli.drainDetection.defectsToggle',
} as const;

const getBernoulliProductConfig = (runtimeConfig?: RuntimeAdpConfig) => {
  const env = runtimeConfig?.env ?? 'dev';
  return BERNOULLI_ADP_BY_ENV[env];
};

const getOrCreateBernoulliAdp = () => {
  if (bernoulliAdp) return bernoulliAdp;
  bernoulliAdp = Adp();
  return bernoulliAdp;
};

export const initBernoulliAdp = (params?: {
  runtimeConfig?: RuntimeAdpConfig;
  hostReferrer?: { id?: string; name?: string; sessionId?: string };
}) => {
  const adp = getOrCreateBernoulliAdp();
  const bernoulliConfig = getBernoulliProductConfig(params?.runtimeConfig);

  if (!bernoulliLoaded) {
    adp.setEnv(bernoulliConfig.env);
    adp.load({
      name: bernoulliConfig.name,
      id: bernoulliConfig.id,
      id_provider: bernoulliConfig.id_provider,
      key: bernoulliConfig.key,
      line_name: params?.runtimeConfig?.line_name ?? bernoulliConfig.line_name,
      meta: {
        env: bernoulliConfig.env,
        region: bernoulliConfig.region,
      },
    });
    bernoulliLoaded = true;
  }

  if (!bernoulliConsentInitialized) {
    const userToken = cookies.get(AUTH_TOKEN_COOKIE_KEY) || undefined;
    bernoulliConsentReady = adp
      .loadUserConsentPreferences(userToken)
      .then(() => undefined)
      .catch(() => undefined);
    bernoulliConsentInitialized = true;
  }

  if (
    params?.hostReferrer?.id &&
    params.hostReferrer.name &&
    params.hostReferrer.sessionId
  ) {
    adp.setProductReferrer(
      {
        id: params.hostReferrer.id,
        name: params.hostReferrer.name,
        sessionId: params.hostReferrer.sessionId,
      },
      'web_application',
    );
  }

  return adp;
};

export const getBernoulliAdp = () => bernoulliAdp;

const sendAdpTrack = (
  adp: AdpAnalytics,
  eventType: 'CLICK_COMPONENT' | 'APPLICATION_VIEW',
  payload: {
    type: string;
    name: string;
    attributes: Record<string, string>;
  },
) => {
  if (bernoulliConsentReady) {
    void bernoulliConsentReady.finally(() => {
      adp.track(eventType, payload);
    });
    return;
  }

  adp.track(eventType, payload);
};

const decodeJwtPayload = (token: string): JwtPayload | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payloadPart = parts[1];
  if (!payloadPart) return null;

  try {
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = atob(padded);
    return JSON.parse(payload) as JwtPayload;
  } catch {
    return null;
  }
};

export const getForgeUserId = (): string | undefined => {
  const token = cookies.get(AUTH_TOKEN_COOKIE_KEY);
  if (!token) return undefined;

  const payload = decodeJwtPayload(token);
  return payload?.sub;
};

export const getTenantId = (): string | undefined => {
  const tenantId = sessionStorage.getItem(TENANT_ID_SESSION_KEY);
  return tenantId || undefined;
};

const baseAttributes = (extra: Record<string, string> = {}) => {
  const attributes: Record<string, string> = {
    'COMMON.APP': 'bernoulli',
    'COMMON.COMMAND_CONTEXT': window.location.pathname,
    ...extra,
  };

  const forgeUserId = getForgeUserId();
  if (forgeUserId) {
    attributes['COMMON.USER_ID'] = forgeUserId;
  }

  const tenantId = getTenantId();
  if (tenantId) {
    attributes['COMMON.TENANT_ID'] = tenantId;
  }

  if (adpContext) {
    if (adpContext.hub_id) attributes['COMMON.HUB_ID'] = adpContext.hub_id;
    if (adpContext.project_id)
      attributes['COMMON.PROJECT_ID'] = adpContext.project_id;
    if (adpContext.team_id) attributes['COMMON.TEAM_ID'] = adpContext.team_id;
    if (adpContext.region) attributes['COMMON.REGION'] = adpContext.region;
    attributes['COMMON.ACCOUNT_TIER'] = adpContext.account_tier;
  }

  return attributes;
};

export const trackAdpEvent = (
  adp: AdpAnalytics | null,
  eventName: string,
  attributes: Record<string, string> = {},
) => {
  if (!adp) return;

  const payload = {
    type: 'command',
    name: 'COMMON.COMMAND_INVOKED',
    attributes: baseAttributes({
      'COMMON.COMMAND_NAME': eventName,
      ...attributes,
    }),
  };
  sendAdpTrack(adp, 'CLICK_COMPONENT', payload);
};

export const trackAdpWaypoint = (
  adp: AdpAnalytics | null,
  waypointName: string,
  attributes: Record<string, string> = {},
) => {
  if (!adp) return;

  const payload = {
    type: 'waypoint',
    name: waypointName,
    attributes: baseAttributes(attributes),
  };
  sendAdpTrack(adp, 'CLICK_COMPONENT', payload);
};

export const trackAdpAppView = (
  adp: AdpAnalytics | null,
  attributes: Record<string, string> = {},
) => {
  if (!adp) return;

  const payload = {
    type: 'view',
    name: 'COMMON.APP_VIEWED',
    attributes: baseAttributes({
      'COMMON.COMMAND_NAME': ADP_EVENTS.APP_ENTRY,
      ...attributes,
    }),
  };
  sendAdpTrack(adp, 'APPLICATION_VIEW', payload);
};
